import { type EndpointResponse } from '@owner/olympus-client'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { type RouteLocationNamedRaw } from 'vue-router'

import { OlympusClient } from '@/OlympusClient'
import { FeatureFlags } from '@/plugins/featureFlags'

import {
  type User,
  type BrandSession,
  type SessionResponse,
  type Session,
} from './types'
import { resetAuthorizer, safeInitializeAuthorizer } from './useAuthorizer'

export const useAuthStore = defineStore('auth', () => {
  const otpSessionId = useLocalStorage<string | null>('otp-session-id', null)

  const setOtpSessionId = (sessionId: string): void => {
    otpSessionId.value = sessionId
  }

  // Route getters to the appropriate store
  const isMultiBrandUser = useLocalStorage<boolean>(
    'owner-is-multi-brand-user',
    false
  )

  const isLocationScopedUser = computed(() => {
    if (!brandSession.value) return false

    return (
      !!brandSession.value.locationIds &&
      brandSession.value.locationIds.length > 1
    )
  })

  const brandSession = useLocalStorage<BrandSession | null>(
    'owner-brand-session',
    null,
    {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    }
  )

  const csrfToken = computed<string | null>(() => {
    if (!session.value) return null

    return session.value.csrfToken ?? null
  })

  const scopes = computed(() => {
    if (!brandSession.value) return []

    return brandSession.value.scopes
  })

  const expiresAt = computed(() => {
    if (!session.value) return null

    return session.value.expires
  })

  const session = useLocalStorage<Session | null>('owner-session-v2', null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  })

  // New V2 user storage
  const user = useLocalStorage<User | null>('owner-user-v2', null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v),
    },
  })

  const acl = computed(() => {
    if (!brandSession.value)
      return {
        brands: [],
        locations: [],
      }

    return {
      brands: brandSession.value.brandId ? [brandSession.value.brandId] : null,
      locations: brandSession.value.locationIds,
    }
  })

  const role = computed(() => {
    if (!brandSession.value) return null

    return brandSession.value.role
  })

  const isSuperAdmin = computed(() => {
    if (!role.value) return false

    return role.value === 'pb:super'
  })

  const home = computed<RouteLocationNamedRaw>(() => {
    if (!user.value) return { name: 'auth.login' }

    if (isSuperAdmin.value) return { name: 'brands' }

    if (brandSession.value?.brandId) {
      return {
        name: 'brand.home',
        params: {
          brandId: brandSession.value.brandId,
        },
      }
    }

    return { name: 'auth.login' }
  })

  const isAuthenticated = computed(() => {
    if (!user.value || !session.value) return false

    return true
  })

  // Helper to set session data from V2 response
  const setSession = ({
    csrfToken,
    defaultBrandSession,
    expiresAt,
    user: sessionUser,
    isMultiBrandUser: newIsMultiBrandUserValue = false,
  }: SessionResponse): void => {
    session.value = {
      csrfToken,
      expires: expiresAt,
      scopes: defaultBrandSession.scopes,
    }

    user.value = sessionUser
    brandSession.value = defaultBrandSession
    isMultiBrandUser.value = newIsMultiBrandUserValue
  }

  const setBrandSession = (newBrandSession: BrandSession): void => {
    brandSession.value = newBrandSession
  }

  const setUserPhone = (phone: string): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        phone,
      }
    }
  }

  const setUser = (userUpdate: Partial<User>): void => {
    if (user.value) {
      user.value = {
        ...user.value,
        ...userUpdate,
      }
    }
  }

  // Route actions to the appropriate store
  const login = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<
    | { status: 'success' }
    | Extract<
        EndpointResponse<'auth.v2.hermes.login'>,
        { status: 'requires-2fa' }
      >
  > => {
    const response = await OlympusClient.auth.v2.hermes.login({
      headers: {
        Authorization: `Basic ${btoa(`${email}:${password}`)}`,
      },
    })

    if (response.status === 'success') {
      setSession(response)
      await safeInitializeAuthorizer(
        response.user,
        response.defaultBrandSession,
        'login'
      )

      return { status: 'success' }
    } else if (response.status === 'requires-2fa') {
      otpSessionId.value = response.sessionId

      return response
    } else {
      throw new Error('Login failed')
    }
  }

  const resendOtp = async ({
    sessionId,
    method,
  }: {
    sessionId?: string | null
    method: 'email' | 'phone'
  }): Promise<
    Extract<EndpointResponse<'auth.v2.hermes.resendOtp'>, { status: 'success' }>
  > => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId) throw new Error('Session ID is required for V2 OTP resend')

    const response = await OlympusClient.auth.v2.hermes.resendOtp({
      body: { method, sessionId },
    })

    if (response.status === 'success') {
      otpSessionId.value = response.sessionId
      return response
    } else {
      throw new Error(response.message)
    }
  }

  const resetPassword = async ({
    password,
    token,
  }: {
    password: string
    token: string
  }): Promise<void> => {
    // Use the V2 resetPassword endpoint
    const response = await OlympusClient.auth.v2.hermes.resetPassword({
      body: {
        password,
      },
      headers: {
        Authorization: `Otp ${token}`,
      },
    })

    // Set the session using the V2 response format
    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'resetPassword'
    )
  }

  const multiFactorLogin = async ({
    sessionId,
    code,
  }: {
    sessionId?: string | null
    code: string
  }): Promise<void> => {
    if (!sessionId) sessionId = otpSessionId.value

    if (!sessionId)
      throw new Error(
        'Session ID is required for V2 multi-factor authentication'
      )

    const response = await OlympusClient.auth.v2.hermes.verifyOtp({
      headers: {
        Authorization: `Otp ${btoa(`${code}:${sessionId}`)}`,
      },
    })

    setSession({
      ...response,
      status: 'success',
    })
    await safeInitializeAuthorizer(
      response.user,
      response.defaultBrandSession,
      'multiFactorLogin'
    )

    otpSessionId.value = null
  }

  const logout = async (): Promise<void> => {
    if (FeatureFlags.isEnabled('hermes.rbac.useAuthorizerGuards')) {
      resetAuthorizer()
    }

    session.value = null
    user.value = null
    brandSession.value = null
    isMultiBrandUser.value = false
    otpSessionId.value = null

    await OlympusClient.auth.v1.hermes
      .logout({
        headers: {},
      })
      .catch(() => undefined)
  }

  const requireUser = (): User => {
    if (!user.value) {
      throw new Error('User not found')
    }

    return user.value
  }

  return {
    acl,
    brandSession,
    csrfToken,
    expiresAt,
    home,
    isAuthenticated,
    isLocationScopedUser,
    isMultiBrandUser,
    isSuperAdmin,
    login,
    logout,
    multiFactorLogin,
    requireUser,
    resendOtp,
    resetPassword,
    role,
    scopes,
    setBrandSession,
    setOtpSessionId,
    setSession,
    setUser,
    setUserPhone,
    user,
  }
})
